// Shared on every page.

// Cart bag + cart drawer. The bag shows the count (no number when empty; on the home page it only
// appears once something's in the cart). Pressing it slides the cart in from the right: the pieces in
// it, a "pairs well with" quick add, the subtotal and checkout.
// On Shopify everything goes through the cart API (/cart.js, /cart/add.js, /cart/change.js). The static
// preview has no store, so it keeps the cart in this tab and uses a small stand-in catalogue.
const cart = document.querySelector('.cart');
if (cart) {
  const PREVIEW = !window.Shopify;
  // stand-in for the store, preview only: what's live, with the same photos the grids use
  const CATALOG = [
    { id: 67696113025341, product_id: 1, title: 'black core cap', price: 3800, image: 'assets/products/black-core-cap.jpg', url: 'product.html', available: true },
    { id: 67696088711485, product_id: 2, title: 'navy heritage hat', price: 4200, image: 'assets/products/navy-heritage-hat.jpg', url: 'product-navy.html', available: false }
  ];
  const KEY = 'taku-preview-lines';
  const lines = {
    get() { try { return JSON.parse(sessionStorage.getItem(KEY)) || []; } catch (e) { return []; } },
    set(l) { try { sessionStorage.setItem(KEY, JSON.stringify(l)); } catch (e) {} }
  };
  const money = c => '$' + (c % 100 ? (c / 100).toFixed(2) : c / 100);
  const amount = c => String(c % 100 ? (c / 100).toFixed(2) : c / 100);   // next to items: no $ (only the subtotal has one)
  const esc = t => String(t ?? '').replace(/[&<>"']/g, ch => `&#${ch.charCodeAt(0)};`);
  const tidy = items => ({
    items,
    count: items.reduce((n, i) => n + i.qty, 0),
    total: items.reduce((n, i) => n + i.price * i.qty, 0)
  });
  const json = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).description || 'couldn’t update the cart');
    return r.json();
  });
  // a product from the recommendations or products feeds, in the shape the drawer uses
  const offer = p => {
    const v = (p.variants || []).find(x => x.available);
    if (!v) return null;
    return {
      id: v.id,
      product_id: p.id,
      title: String(p.title).toLowerCase(),
      price: typeof v.price === 'string' ? Math.round(parseFloat(v.price) * 100) : v.price,
      image: p.featured_image?.src || p.featured_image || p.images?.[0]?.src || '',
      url: p.url || `/products/${p.handle}`
    };
  };

  const api = PREVIEW ? {
    async get() {
      return tidy(lines.get().map(l => {
        const c = CATALOG.find(x => x.id === l.id);
        return c && { ...c, key: String(c.id), qty: l.qty };
      }).filter(Boolean));
    },
    async add(id, qty) {
      const l = lines.get(), hit = l.find(x => x.id === id);
      hit ? (hit.qty += qty) : l.push({ id, qty });
      lines.set(l);
    },
    async change(key, qty) {
      lines.set(lines.get().map(l => (String(l.id) === key ? { ...l, qty } : l)).filter(l => l.qty > 0));
    },
    async suggest(items) {
      return CATALOG.filter(c => c.available && !items.some(i => i.id === c.id));
    }
  } : {
    async get() {
      const c = await fetch('/cart.js').then(r => r.json());
      return tidy(c.items.map(i => ({
        key: i.key, id: i.variant_id, product_id: i.product_id,
        title: i.product_title.toLowerCase(),
        variant: i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title.toLowerCase() : '',
        price: i.final_price, qty: i.quantity, image: i.image, url: i.url
      })));
    },
    add: (id, qty) => json('/cart/add.js', { items: [{ id, quantity: qty }] }),
    change: (key, qty) => json('/cart/change.js', { id: key, quantity: qty }),
    // "pairs well with": the pairings set in Shopify (Search & Discovery's complementary products) for
    // what's in the cart, then similar pieces, then anything live. Never something sold out or
    // already in the cart.
    async suggest(items) {
      const have = new Set(items.map(i => i.product_id));
      const first = items[0]?.product_id;
      const feeds = first ? [
        `/recommendations/products.json?product_id=${first}&intent=complementary&limit=6`,
        `/recommendations/products.json?product_id=${first}&intent=related&limit=6`
      ] : [];
      feeds.push('/products.json?limit=30');
      for (const url of feeds) {
        try {
          const list = ((await fetch(url).then(r => r.json())).products || [])
            .filter(p => !have.has(p.id)).map(offer).filter(Boolean);
          if (list.length) return list;
        } catch (e) {}
      }
      return [];
    }
  };

  const showCart = n => {
    cart.querySelector('.cart__count').textContent = n > 0 ? n : '';   // empty bag, no number
    cart.setAttribute('aria-label', `cart, ${n} item${n == 1 ? '' : 's'}`);
    // home page: only when something's in the cart; every other page: always
    cart.hidden = !(n > 0) && !document.body.classList.contains('page');
    cart.dataset.count = n;
  };
  const pop = () => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    cart.classList.remove('is-popping');
    void cart.offsetWidth;   // restart the pop if it's already running
    cart.classList.add('is-popping');
    cart.addEventListener('animationend', () => cart.classList.remove('is-popping'), { once: true });
  };

  // the drawer, the same pen as the rest of the site
  const pen = 'fill="none" stroke="currentColor" stroke-width="2.7" stroke-linecap="round" stroke-linejoin="round"';
  const mark = {
    plus: `<svg viewBox="0 0 20 20" aria-hidden="true" ${pen}><path d="M3.2 10.4 C7.6 9.6 12.4 10.3 16.8 9.7"/><path d="M10.3 3.1 C9.7 7.8 10.4 12.6 9.8 16.9"/></svg>`,
    minus: `<svg viewBox="0 0 20 20" aria-hidden="true" ${pen}><path d="M3.2 10.4 C7.6 9.6 12.4 10.3 16.8 9.7"/></svg>`,
    x: `<svg viewBox="0 0 24 24" aria-hidden="true" ${pen}><path d="M4.6 5.2 C9.4 9.6 14.2 14.6 19.4 19.1"/><path d="M19.2 4.8 C14.6 9.8 9.8 14.2 4.8 19.4"/></svg>`
  };
  const drawer = document.createElement('dialog');
  drawer.className = 'cart-drawer';
  drawer.setAttribute('aria-labelledby', 'cart-drawer-title');
  drawer.innerHTML = `
    <div class="cart-drawer__panel">
      <div class="cart-drawer__head">
        <h2 class="cart-drawer__title" id="cart-drawer-title">cart</h2>
        <button class="cart-drawer__close" type="button" aria-label="close cart">${mark.x}</button>
      </div>
      <div class="cart-drawer__body">
        <p class="cart-drawer__empty">your cart is empty</p>
        <ul class="cart-drawer__lines" aria-label="in your cart"></ul>
        <section class="cart-drawer__pairs" aria-labelledby="cart-pairs-title" hidden>
          <h3 class="cart-drawer__label" id="cart-pairs-title">pairs well with</h3>
          <div class="cart-pair"></div>
        </section>
      </div>
      <div class="cart-drawer__foot" hidden>
        <p class="cart-drawer__total"><span>subtotal</span><span class="cart-drawer__subtotal"></span></p>
        <p class="cart-drawer__note">shipping and taxes at checkout</p>
        <a class="cart-drawer__checkout" href="/checkout">checkout</a>
        <p class="cart-drawer__error" role="alert" hidden></p>
      </div>
      <p class="visually-hidden" aria-live="polite"></p>
    </div>`;
  document.body.append(drawer);
  const $d = s => drawer.querySelector(s);
  const list = $d('.cart-drawer__lines'), pairs = $d('.cart-drawer__pairs'), pair = $d('.cart-pair');
  const foot = $d('.cart-drawer__foot'), errBox = $d('.cart-drawer__error'), live = $d('[aria-live]');
  let current = { items: [], count: 0, total: 0 }, suggestion = null;

  const fail = x => { errBox.textContent = x.message.toLowerCase(); errBox.hidden = false; };
  const render = async () => {
    current = await api.get();
    showCart(current.count);
    list.innerHTML = current.items.map(i => `
      <li class="cart-line" data-key="${esc(i.key)}" data-qty="${i.qty}">
        <a class="cart-line__img" href="${esc(i.url)}" tabindex="-1"><img src="${esc(i.image)}" alt="" width="88" height="88"></a>
        <div class="cart-line__info">
          <a class="cart-line__name" href="${esc(i.url)}">${esc(i.title)}</a>
          ${i.variant ? `<span class="cart-line__variant">${esc(i.variant)}</span>` : ''}
          <div class="cart-line__row">
            <div class="cart-line__qty" role="group" aria-label="quantity, ${esc(i.title)}">
              <button class="cart-line__step" type="button" data-step="-1" aria-label="one less"${i.qty === 1 ? ' disabled' : ''}>${mark.minus}</button>
              <span class="cart-line__count">${i.qty}</span>
              <button class="cart-line__step" type="button" data-step="1" aria-label="one more"${i.qty >= 9 ? ' disabled' : ''}>${mark.plus}</button>
            </div>
            <span class="cart-line__price">${amount(i.price * i.qty)}</span>
          </div>
          <button class="cart-line__remove" type="button">remove</button>
        </div>
      </li>`).join('');
    $d('.cart-drawer__empty').hidden = current.items.length > 0;
    foot.hidden = !current.items.length;
    $d('.cart-drawer__subtotal').textContent = money(current.total);
    suggestion = (await api.suggest(current.items).catch(() => []))[0] || null;
    pairs.hidden = !suggestion;
    if (suggestion) pair.innerHTML = `
      <a class="cart-pair__img" href="${esc(suggestion.url)}" tabindex="-1"><img src="${esc(suggestion.image)}" alt="" width="72" height="72"></a>
      <div class="cart-pair__info">
        <a class="cart-pair__name" href="${esc(suggestion.url)}">${esc(suggestion.title)}</a>
        <span class="cart-pair__price">${amount(suggestion.price)}</span>
      </div>
      <button class="cart-pair__add" type="button" aria-label="add ${esc(suggestion.title)} to cart">add</button>`;
  };

  const open = async () => {
    errBox.hidden = true;
    await render().catch(fail);
    if (!drawer.open) drawer.showModal();
  };
  // slide back out to the right, then close
  const shut = () => {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return drawer.close();
    drawer.classList.add('is-closing');
    drawer.addEventListener('animationend', () => { drawer.classList.remove('is-closing'); drawer.close(); }, { once: true });
  };
  cart.addEventListener('click', e => { e.preventDefault(); open(); });
  $d('.cart-drawer__close').addEventListener('click', shut);
  drawer.addEventListener('cancel', e => { e.preventDefault(); shut(); });        // Esc
  drawer.addEventListener('click', e => { if (e.target === drawer) shut(); });    // the greyed page
  drawer.addEventListener('close', () => cart.focus());

  const busy = async (fn, msg) => {
    errBox.hidden = true;
    drawer.setAttribute('aria-busy', 'true');
    try { await fn(); await render(); if (msg) live.textContent = msg; }
    catch (x) { fail(x); }
    finally { drawer.removeAttribute('aria-busy'); }
  };
  list.addEventListener('click', e => {
    const line = e.target.closest('.cart-line');
    if (!line) return;
    const key = line.dataset.key, qty = Number(line.dataset.qty);
    const step = e.target.closest('.cart-line__step');
    if (step && !step.disabled) busy(() => api.change(key, Math.max(1, qty + Number(step.dataset.step))));
    if (e.target.closest('.cart-line__remove')) busy(() => api.change(key, 0), 'removed');
  });
  // quick add: straight into the cart, then it drops out of "pairs well with"
  pair.addEventListener('click', e => {
    if (!e.target.closest('.cart-pair__add') || !suggestion) return;
    const s = suggestion;
    busy(async () => { await api.add(s.id, 1); pop(); }, `${s.title} added`);
  });
  // the preview can't check out
  $d('.cart-drawer__checkout').addEventListener('click', e => {
    if (!PREVIEW) return;
    e.preventDefault();
    fail(new Error('checkout opens on the live store'));
  });

  const refresh = () => api.get().then(c => { current = c; showCart(c.count); }).catch(() => {});
  refresh();
  // product.js adds without reloading the page, then the bag updates
  window.takuCart = {
    refresh,
    open,
    show: showCart,
    count: () => current.count,
    add: async (id, qty) => { await api.add(id, qty); await refresh(); }
  };
}

// Inner pages: as soon as the four page words no longer fit on one row beside the logo (any window
// size, desktop included), they move into a menu behind a drawn three-line button, top left,
// opposite the bag. Built from the header's own links, so the words only live in one place;
// without JS the stacked words beside the logo stay as they are.
const header = document.querySelector('.page-header');
if (header) {
  const pen = 'fill="none" stroke="currentColor" stroke-width="2.7" stroke-linecap="round" stroke-linejoin="round"';
  const button = document.createElement('button');
  button.className = 'menu-toggle';
  button.type = 'button';
  button.setAttribute('aria-label', 'menu');
  button.setAttribute('aria-haspopup', 'dialog');
  button.innerHTML = `<svg viewBox="0 0 28 22" aria-hidden="true" ${pen}>
    <path class="menu-toggle__line" d="M3 4.6 C9.6 3.8 17.4 5.2 25 4.2"/>
    <path class="menu-toggle__line" d="M3.4 11.2 C10.2 11.9 17.8 10.5 24.6 11.4"/>
    <path class="menu-toggle__line" d="M3 17.9 C9.8 17.1 17 18.6 25.2 17.6"/></svg>`;

  const menu = document.createElement('dialog');
  menu.className = 'site-menu';
  menu.setAttribute('aria-label', 'menu');
  const links = [...header.querySelectorAll('.page-nav a')].map(a => a.cloneNode(true));
  const nav = document.createElement('nav');
  nav.className = 'site-menu__nav';
  links.forEach(a => nav.append(a));
  const close = document.createElement('button');
  close.className = 'menu-toggle site-menu__close';
  close.type = 'button';
  close.setAttribute('aria-label', 'close menu');
  close.innerHTML = `<svg viewBox="0 0 28 22" aria-hidden="true" ${pen}>
    <path class="menu-toggle__line" d="M6.6 3.4 C11 7.6 16 12.6 21.4 18.4"/>
    <path class="menu-toggle__line" d="M21.2 3.2 C16.6 8.2 11.8 13 6.8 18.6"/></svg>`;
  menu.append(close, nav);

  header.prepend(button);
  document.body.append(menu);
  document.body.classList.add('has-menu');
  button.addEventListener('click', () => menu.showModal());
  close.addEventListener('click', () => menu.close());
  menu.addEventListener('close', () => button.focus());

  // does each pair of words still fit its side of the logo on one line, with the full spacing?
  // At 760px and narrower they'd have to stack, so that's always the menu.
  const navs = [...header.querySelectorAll('.page-nav')];
  const narrow = matchMedia('(max-width: 760px)');
  const fit = () => {
    document.body.classList.remove('menu-collapsed');
    if (narrow.matches) {
      document.body.classList.add('menu-collapsed');
      return;
    }
    // measured word to word: the left pair hugs the logo, so it spills out to the left, which
    // scrollWidth doesn't count
    const tight = navs.some(n => {
      const a = n.firstElementChild.getBoundingClientRect(), b = n.lastElementChild.getBoundingClientRect();
      return b.right - a.left > n.clientWidth + 1;
    });
    document.body.classList.toggle('menu-collapsed', tight);
    if (!tight && menu.open) menu.close();
  };
  // Scrolling down: the header fades to nothing, then the three lines draw themselves in, top to
  // bottom, each starting once the line above is a quarter drawn. Tied to the scroll, so scrolling
  // back up un-draws them and brings the header back. Where the words are already behind the
  // lines (narrow screens) the lines stay drawn: they're the only way to the menu.
  const lines = [...button.querySelectorAll('.menu-toggle__line')];
  lines.forEach(l => { l.setAttribute('pathLength', 1); l.style.strokeDasharray = 1; });
  const faders = [...header.children].filter(el => el !== button);
  const FADE = 28;    // px from the very top: the header only shows within this, so scrolling back
                      // up it stays gone until you're nearly at the top
  const DRAW = 220;   // px of scrolling (after the fade) to draw all three
  let queued = false;
  const onScroll = () => {
    queued = false;
    const y = scrollY;
    const o = Math.max(0, 1 - y / FADE);
    faders.forEach(el => (el.style.opacity = o));   // not the header itself: the lines live in it
    const t = document.body.classList.contains('menu-collapsed') ? 1
      : Math.min(1, Math.max(0, (y - FADE) / DRAW));
    // each line takes 2/3 of the run; line 2 starts 1/4 into line 1, line 3 1/4 into line 2
    lines.forEach((l, i) => {
      const p = Math.min(1, Math.max(0, t * 1.5 - i * 0.25));
      l.style.strokeDashoffset = 1 - p;
      l.style.opacity = p > 0 ? 1 : 0;   // hide the round-cap dot of an undrawn line
    });
    document.body.classList.toggle('menu-scrolled', t > 0);
  };
  addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(onScroll); } }, { passive: true });

  const refit = () => { fit(); onScroll(); };
  new ResizeObserver(refit).observe(header);
  document.fonts?.ready.then(refit);
  refit();
}
